import type { SearchApiDocument } from '../../../api/model/response/documents/SearchResult';
import type {
  Attachment,
  I3File,
} from '../../../api/model/response/documents/public/Document';
import { getLocale, getLocaleCode } from '../../../helpers/locale';
import { formatDate } from '../../../components/result-page/result-cards/utils/DateFormatter';
import {badgesSortedByPriority} from '../../helpers/getSortedMarketingBadges';
export type I3FileType = I3File | I3File[];

export interface MiddlewareRequestParams {
    language: string
    country: string
}


function i3DocumentsMap(
  documents: any,  //eslint-disable-line
  params: MiddlewareRequestParams
): Array<SearchApiDocument> {
    const sourcesWithDates = [
      'BSL_DOC',
      'TRACEPARTS',
      'DIGEST_PLUS',
      'FAQ_BFO',
      'FAQ_INQUIRA',
      'FAQ_VIDEO',
      'ECAT_BSL_SUB',
      'caas-myschneider',
      'ELE_UNV'
    ];

    function displayDate(document): string {
        const locale = getLocale(document);
        const localeCode = getLocaleCode(params);
        if (sourcesWithDates.includes(document.source)) {
          return document.localeDate ? formatDate(document.localeDate, localeCode) : formatDate(document.date || document.modifyDate, localeCode);
        } else if (document[locale] && document[locale].commStatus) {
          switch (document[locale].commStatus) {
          case '05':
          case '14':
          case '16':
          case '18':
            return document.localeDate ? formatDate(document.localeDate, localeCode) : formatDate(document.date, localeCode);
          default:
            return '';
          }
        }
        return '';
    }

    function mergeDocumentWithLocaleData(el, locale) {
        locale.url = locale.openUrl;
        delete locale.openUrl;
        locale.category = {
          CT_CAT: locale.f3Cat || locale.f2Cat || locale.f1Cat,
        };
        if(el.f2Symbol && el.f2Symbol === 'Substitution') {
          locale.commStatus = '08';
        }
        if(locale.eNumber){
          locale.enumber = locale.eNumber;
          delete locale.eNumber;
        }
        if (locale?.badgeTypes && locale?.badgeTypes?.length > 1) {
          const sortedBadges = badgesSortedByPriority(locale?.badgeTypes);
          locale.badgeTypes = [sortedBadges[0]];
        }
        return { ...el, ...locale };
    }

    function mapI3FileToAttachments(file: I3FileType): Array<Attachment> {
        const files: I3File[] = (file as I3File[]).length
          ? (file as I3File[])
          : ([file] as I3File[]);
        return files.map((f) => {
            return {
              extension: f.ext.startsWith('.') ? f.ext.slice(1) : f.ext,
              fileName: f.name,
              version: f.version,
              url: f.downloadUrl,
              length: f.size > 0 ? (f.size / (1024 * 1024)).toFixed(2) + ' MB' : '',
            };
        });
    }

    function getLocaleData(el: any, params: MiddlewareRequestParams) {  //eslint-disable-line
        const localeKey = `${params.language}_${params.country.toUpperCase()}`;
        if (el.hasOwnProperty(localeKey)) return el[localeKey];

        return el['en_WW'];
    }

    return documents.map((el) => {
        const localeData = getLocaleData(el, params);

        let document = {
          ...el,
          date: displayDate(el),
          source: el.source,
        };

        if (localeData) {
          document = mergeDocumentWithLocaleData(document, localeData);
        }
        if (document.image) {
          document.thumbnailUrl = document.image;
          delete document.image;
        }

        if (document.file) {
          document.attachments = mapI3FileToAttachments(
                document.file as I3FileType
          );
          delete document.file;
        }

        if(document.downloadUrl) {
          document.attachments = [{url: document.downloadUrl}];
          delete document.downloadUrl;
        }

        if(document.id &&
          document.category &&
          document.source != 'SUPP_TOOL' &&
          document.source != 'MARKETO') {
          // idHighlighted field value can contain html tag with 'highlight' class
          document.sku = document.idHighlighted;
          return document;
        }

        if(document.source === 'ECAT_BSL' && document.range) {
          if (document.range.length > 0) {
            document.productRange = document.range.map(range => {
                return {
                  name: range?.translation,
                  url: range?.url
                };
            });
          } else if(document.range?.url) {
            document.productRange = [{
              name: document.range?.translation,
              url: document.range?.url
            }];
          }
          delete document.range;
        }


        return document;
    });
}

export default i3DocumentsMap;
